import type { PageType } from '@maestro/core'
import type { CartEntry, Product } from '@maestro/data-layer'
import type { Session } from 'next-auth'

export interface Item {
  product: Product
  quantity?: number
  variantId?: string
  position?: number
  queryID?: string
  atcType?: string
  affiliation?: string
}

export interface CookieConsent {
  marketing?: boolean // targeting cookies
  necessary?: boolean
  preferences?: boolean // functional cookies
  socialMedia?: boolean
  statistics?: boolean // performance/analytical cookies
}

/**
 * Enum defining all supported client-side event types
 * Used with the Event Bus for type-safe event publishing and subscription
 */
export enum EventType {
  ACCORDION = 'ACCORDION',
  ALERT_SHOWN = 'ALERT_SHOWN',
  ARTICLE_CLICKED = 'ARTICLE_CLICKED',
  ARTICLE_LIST_VIEWED = 'ARTICLE_LIST_VIEWED',
  ARTICLE_VIEWED = 'ARTICLE_VIEWED',
  BALL_MENU_CLICKED = 'BALL_MENU_CLICKED',
  BREADCRUMBS_CLICKED = 'BREADCRUMBS_CLICKED',
  CATEGORY_VIEWED = 'CATEGORY_VIEWED',
  CART_SCROLL_COMPLETE = 'CART_SCROLL_COMPLETE',
  CART_VIEWED = 'CART_VIEWED',
  CHECKOUT_STARTED = 'CHECKOUT_STARTED',
  COLLECT_AT_STORE_ORDER_COMPLETED = 'COLLECT_AT_STORE_ORDER_COMPLETED',
  COOKIE_CONSENT_DECLINED = 'COOKIE_CONSENT_DECLINED',
  COOKIE_CONSENT_DIALOG_OPENED = 'COOKIE_CONSENT_DIALOG_OPENED',
  COOKIE_CONSENT_GRANTED = 'COOKIE_CONSENT_GRANTED',
  COOKIE_CONSENT_SET = 'COOKIE_CONSENT_SET', // fired when consent set by cookie mangement vendor
  CTA_CLICKED = 'CTA_CLICKED',
  ERROR_MESSAGE = 'ERROR_MESSAGE_SHOWN',
  FOOTER_LINK_CLICKED = 'FOOTER_LINK_CLICKED',
  HELPBOX = 'HELPBOX',
  IS_LOYALTY_CLUB_MEMBER = 'IS_LOYALTY_CLUB_MEMBER',
  JOINED_CLUB = 'JOINED_CLUB',
  LEFT_CLUB = 'LEFT_CLUB',
  MENU_CLICKED = 'MENU_CLICKED',
  ORDER_COMPLETED = 'ORDER_COMPLETED',
  PAGE_VIEW = 'PAGE_VIEW',
  PAYMENT_INFO_ADDED = 'PAYMENT_INFO_ADDED',
  PRODUCT_ADDED_TO_CART = 'PRODUCT_ADDED_TO_CART',
  PRODUCT_ADDED_TO_WISHLIST = 'PRODUCT_ADDED_TO_WISHLIST',
  PRODUCT_CLICKED = 'PRODUCT_CLICKED',
  PRODUCT_LIST_VIEWED = 'PRODUCT_LIST_VIEWED',
  PRODUCT_REMOVED_FROM_CART = 'PRODUCT_REMOVED_FROM_CART',
  PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK = 'PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK',
  PRODUCT_VIEWED = 'PRODUCT_VIEWED',
  PRODUCTS_FILTERED = 'PRODUCTS_FILTERED',
  PRODUCTS_SORTED = 'PRODUCTS_SORTED',
  PROMOTION_SELECTED = 'PROMOTION_SELECTED',
  PROMOTION_VIEWED = 'PROMOTION_VIEWED',
  REMOVED_ALL_PRODUCTS_FROM_CART = 'REMOVED_ALL_PRODUCTS_FROM_CART',
  SEARCH = 'SEARCH',
  SEARCH_ARTICLES_VIEWED = 'SEARCH_ARTICLES_VIEWED',
  SEARCH_SUGGESTIONS_CLICKED = 'SEARCH_SUGGESTIONS_CLICKED',
  SHIPPING_INFO_ADDED = 'SHIPPING_INFO_ADDED',
  SLIDE_IN_CLOSED = 'SLIDE_IN_CLOSED',
  SLIDE_IN_OPENED = 'SLIDE_IN_OPENED',
  STORE_SELECTED = 'STORE_SELECTED',
  SUB_MENU_CLICKED = 'SUB_MENU_CLICKED',
  TOP_NAVIGATION_CLICKED = 'TOP_NAVIGATION_CLICKED',
  USER_IDENTIFIED = 'USER_IDENTIFIED',
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  USER_LOGGED_OUT = 'USER_LOGGED_OUT',
  USER_SIGNED_UP = 'USER_SIGNED_UP',
}

/**
 * Type definitions for event payloads
 * Maps each event type to its expected data structure
 */
export interface EventPayloadType {
  [EventType.ACCORDION]: {
    accordionTitle: string
    action: string
    location: string
  }
  [EventType.ALERT_SHOWN]: {
    alertType: string
    alertTitle: string
  }
  [EventType.BALL_MENU_CLICKED]: {
    click_text: string
  }
  [EventType.ARTICLE_CLICKED]: {
    objectID: string
    position?: number
    queryID?: string
    searchServiceIndex?: string
    authenticatedUserToken?: string
  }
  [EventType.ARTICLE_LIST_VIEWED]: {
    objectIDs: string[]
    searchServiceIndex?: string
    authenticatedUserToken?: string
  }
  [EventType.ARTICLE_VIEWED]: {
    objectID: string
    searchServiceIndex?: string
    authenticatedUserToken?: string
    category: string
    title: string
    id: string
    categoryId: string
    url: string
  }
  [EventType.BREADCRUMBS_CLICKED]: {
    position: string
    click_text: string
    click_url?: string
  }
  [EventType.CATEGORY_VIEWED]: {
    category: string
    allowedForTracking: boolean
  }
  [EventType.CART_SCROLL_COMPLETE]: {
    cartSize: number
    value: number
  }
  [EventType.CART_VIEWED]: {
    items: Item[]
    currency: string
    value: number
    deliveryMethod: string
    checkoutMethod?: string
    coupon?: string
  }
  [EventType.CHECKOUT_STARTED]: {
    items: Item[]
    coupon?: string
    currency: string
    value: number
    deliveryMethod: string
    checkoutMethod?: string
  }
  [EventType.COLLECT_AT_STORE_ORDER_COMPLETED]: {
    items: Item[]
    step: string
    transactionId?: string
    value: number
    tax: number
    shipping: number
    currency: string
    coupon?: string
    shippingTier: string
    paymentType: string
    authenticatedUserToken: string
    searchServiceIndex?: string
    deliveryMethod?: string
    checkoutMethod?: string
  }
  [EventType.COOKIE_CONSENT_DECLINED]: {
    cookieConsent: CookieConsent
  }
  [EventType.COOKIE_CONSENT_DIALOG_OPENED]: {
    cookieConsent: CookieConsent
  }
  [EventType.COOKIE_CONSENT_GRANTED]: {
    cookieConsent: CookieConsent
  }
  [EventType.COOKIE_CONSENT_SET]: {
    cookieConsent: CookieConsent
  }
  [EventType.CTA_CLICKED]: {
    location: string
    click_text: string
    click_url?: string
  }
  [EventType.ERROR_MESSAGE]: {
    errorType: string
    errorTitle: string
  }
  [EventType.FOOTER_LINK_CLICKED]: {
    level1: string
    level2: string
    click_url: string
    click_text: string
  }
  [EventType.HELPBOX]: {
    alertType: string
    alertTitle: string
  }
  [EventType.IS_LOYALTY_CLUB_MEMBER]: {
    hashedEmail: string
    isClubMember: boolean
  }
  [EventType.JOINED_CLUB]: {
    user_id?: string
    user_data?: {
      email_address?: string
      phone_number?: string
      first_name?: string
      last_name?: string
      street?: string
      city?: string
      region?: string
      postal_code?: string
      country?: string
      total_order_count?: number
      total_lifetime_value?: number
    }
  }
  [EventType.LEFT_CLUB]: {
    action?: string
    user_id?: string
    user_data?: {
      email_address?: string
      phone_number?: string
      first_name?: string
      last_name?: string
      street?: string
      city?: string
      region?: string
      postal_code?: string
      country?: string
      total_order_count?: number
      total_lifetime_value?: number
    }
  }
  [EventType.MENU_CLICKED]: {
    level1?: string
    level2?: string
    level3?: string
  }
  [EventType.ORDER_COMPLETED]: {
    items: Item[]
    transactionId: string
    value: number
    tax: number
    shipping: number
    currency: string
    coupon?: string
    shippingTier: string
    paymentType: string
    authenticatedUserToken: string
    searchServiceIndex?: string
    deliveryMethod?: string
    checkoutMethod?: string
  }
  [EventType.PAGE_VIEW]: {
    pageType: PageType
    id?: string
    isHashChanged: boolean
    isRenderedRecommendations: boolean
    session?: Session | null
    hash: string
    cart?: CartEntry[]
    searchResultType?: string
  }
  [EventType.PAYMENT_INFO_ADDED]: {
    items: Item[]
    coupon?: string
    paymentType?: string
    currency: string
    value: number
    deliveryMethod: string
    checkoutMethod?: string
  }
  [EventType.PRODUCT_ADDED_TO_CART]: {
    item: Item
    listId?: string
    listName?: string
    listType?: string
    searchServiceIndex?: string
    authenticatedUserToken: string
    queryID?: string
    cart: CartEntry[]
    triggeredFrom: string
    isMember: boolean
  }
  [EventType.PRODUCT_ADDED_TO_WISHLIST]: {
    item: Item
    listId?: string
    listName?: string
    listType?: string
    searchServiceIndex?: string
    authenticatedUserToken: string
    queryID?: string
  }
  [EventType.PRODUCT_CLICKED]: {
    item: Item
    listId?: string
    listName: string
    listType: string
    searchServiceIndex?: string
    authenticatedUserToken: string
    queryID?: string
  }
  [EventType.PRODUCT_LIST_VIEWED]: {
    items: Item[]
    listId?: string
    listName: string
    listType: string
    searchServiceIndex?: string
    authenticatedUserToken: string
  }
  [EventType.PRODUCT_REMOVED_FROM_CART]: {
    item: Item
    cart: CartEntry[]
    isMember: boolean
  }
  [EventType.REMOVED_ALL_PRODUCTS_FROM_CART]: {
    items: Item[]
    cart: CartEntry[]
    location: string
  }
  [EventType.PRODUCT_SUBSCRIBED_TO_OUT_OF_STOCK]: {
    item: Item
    searchServiceIndex?: string
    authenticatedUserToken: string
    queryID?: string
  }
  [EventType.PRODUCT_VIEWED]: {
    item: Item
    searchServiceIndex?: string
    authenticatedUserToken: string
    triggeredFrom?: string
  }
  [EventType.PRODUCTS_FILTERED]: {
    filterName: string
    filterType: string
    filterValue: string
    filterValueType: string
    searchServiceIndex?: string
    authenticatedUserToken: string
  }
  [EventType.PRODUCTS_SORTED]: {
    sortBy: string
    sortOrder: string
  }
  [EventType.PROMOTION_SELECTED]: {
    creative_name?: string
    creative_slot?: string
    promotion_id?: string
    promotion_name?: string
  }
  [EventType.PROMOTION_VIEWED]: {
    creative_name?: string
    creative_slot?: string
    promotion_id?: string
    promotion_name?: string
  }
  [EventType.SEARCH]: {
    searchTerm: string
    numberOfResults: number
  }
  [EventType.SEARCH_ARTICLES_VIEWED]: {
    event_type: string
    name: string
    url: string
  }
  [EventType.SEARCH_SUGGESTIONS_CLICKED]: {
    searchSection: string
    searchTerm: string
    clickUrl: string
  }
  [EventType.SHIPPING_INFO_ADDED]: {
    currency: string
    deliveryMethod: string
    value: number
    checkoutMethod?: string
    items: Item[]
    coupon?: string
    shippingTier?: string
  }
  [EventType.SLIDE_IN_CLOSED]: {
    title: string
    slideInType?: string
    action: 'close'
  }
  [EventType.SLIDE_IN_OPENED]: {
    title: string
    slideInType?: string
    action: 'open'
  }
  [EventType.STORE_SELECTED]: {
    currentStore: string
    previousStore?: string
  }
  [EventType.SUB_MENU_CLICKED]: {
    level1: string
    level2: string
    level3: string
  }
  [EventType.TOP_NAVIGATION_CLICKED]: {
    itemClicked: string
  }
  [EventType.USER_IDENTIFIED]: {
    hashedEmail: string
  }
  [EventType.USER_LOGGED_IN]: {
    hashedEmail: string
    method: string
    user_id?: string
    user_data?: {
      email_address?: string
      phone_number?: string
      first_name?: string
      last_name?: string
      street?: string
      city?: string
      region?: string
      postal_code?: string
      country?: string
      total_order_count?: number
      total_lifetime_value?: number
    }
  }
  [EventType.USER_LOGGED_OUT]: {
    user_id?: string
    user_data?: {
      email_address?: string
      phone_number?: string
      first_name?: string
      last_name?: string
      street?: string
      city?: string
      region?: string
      postal_code?: string
      country?: string
      total_order_count?: number
      total_lifetime_value?: number
    }
  }
  [EventType.USER_SIGNED_UP]: {
    hashedEmail: string
    user_id?: string
    method?: string
    user_data?: {
      email_address?: string
      phone_number?: string
      first_name?: string
      last_name?: string
      street?: string
      city?: string
      region?: string
      postal_code?: string
      country?: string
      total_order_count?: number
      total_lifetime_value?: number
    }
  }
}

/**
 * Type helper to get the payload type for a specific event
 */
export type EventPayload<T extends EventType> = EventPayloadType[T]

export interface EventListType {
  listId?: string
  listName?: string
  listType?: string
}

export interface EventSearchType {
  searchIndex?: string
  queryID?: string
}

export interface EventPropsType {
  productIndex?: number
  eventListProps?: EventListType
  eventSearchProps?: EventSearchType
  triggeredFrom: string
}

export enum TopNavigationTitle {
  CART = 'cart',
  LOGIN = 'login',
  LOGO = 'logo',
  SEARCH = 'search',
  STORE = 'store',
  WISHLIST = 'wishlist',
}

export enum TriggeredFrom {
  PDP = 'PDP',
  SEARCH = 'search',
  PLP = 'PLP',
  CAROUSEL = 'carousel',
  WISHLIST = 'wishlist',
  ORDER = 'order',
  CART = 'cart',
}
